import { Box } from '@mui/material';
import { tokens } from '../../../theme';

type FormSwitchProps<T extends string> = {
  /** Names the group for a screen reader — what the options choose between. */
  label: string;
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
};

/**
 * A pill switch between a few forms of the same instructions.
 *
 * Controlled, so one switch can drive every block in a column rather than each
 * block owning its own.
 */
export const FormSwitch = <T extends string>({
  label,
  options,
  value,
  onChange,
}: FormSwitchProps<T>) => (
  <Box
    role="group"
    aria-label={label}
    sx={{
      display: 'inline-flex',
      flexWrap: 'wrap',
      backgroundColor: 'surface.sunken',
      borderRadius: `${String(tokens.radius.full)}px`,
      padding: '2px',
      width: 'fit-content',
    }}
  >
    {options.map((option) => (
      <Box
        key={option.value}
        component="button"
        type="button"
        aria-pressed={value === option.value}
        onClick={() => {
          onChange(option.value);
        }}
        sx={{
          border: 0,
          cursor: 'pointer',
          fontFamily: tokens.fontFamily.body,
          fontSize: 13,
          fontWeight: value === option.value ? 600 : 400,
          paddingInline: 3,
          paddingBlock: 1,
          borderRadius: `${String(tokens.radius.full)}px`,
          backgroundColor: value === option.value ? 'surface.page' : 'transparent',
          color: value === option.value ? 'text.primary' : 'text.secondary',
        }}
      >
        {option.label}
      </Box>
    ))}
  </Box>
);
